import { useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type {
  IVideo,
  IVideoCategory,
  IVideoResultCreate,
  IVideoResultItem,
  IVideoTagResultItem,
  IVideoUpdate,
} from '~/types/video'

import {
  addVideoToFavourite,
  createVideo,
  deleteVideoFromFavourite,
  listAllVideoCategories,
  listAllVideoTag,
  listUserVideo,
  updateVideo,
  updateVideoThumbnail,
} from '~/api/videos'

////////// KEYS //////////

export const keys = {
  all: ['videos'] as const,
  dynamic: (queryKey: string) => [...keys.all, 'dynamic', queryKey] as const,
  optionCategories: () => [...keys.all, 'option-categories'] as const,
  optionTags: () => [...keys.all, 'option-tags'] as const,
  user: () => [...keys.all, 'user'] as const,
}

////////// QUERIES //////////

export const useUserVideosListQuery = (
  options: {
    enabled?: boolean
    search?: string
  } = { enabled: true }
): UseQueryResult<IPaginatedResponse<IVideoResultItem>> => {
  return useQuery({
    enabled: options.enabled,
    queryFn: () => listUserVideo({ q: options.search }),
    queryKey: keys.user(),
  })
}

export const useDynamicCategoryVideosListQuery = (
  queryKey: string,
  queryFn: (search?: string) => Promise<IPaginatedResponse<IVideoResultItem>>,
  search?: string,
  options?: {
    enabled?: boolean
  }
): UseQueryResult<IPaginatedResponse<IVideoResultItem>> => {
  return useQuery({
    enabled: options?.enabled,
    queryFn: () => queryFn(search),
    queryKey: keys.dynamic(queryKey),
  })
}

export const useVideosCategoriesListQuery = (): UseQueryResult<IVideoCategory[]> => {
  return useQuery({ queryFn: listAllVideoCategories, queryKey: keys.optionCategories() })
}

export const useVideosTagsListQuery = (): UseQueryResult<IOption[]> => {
  return useQuery({ queryFn: listAllVideoTag, queryKey: keys.optionTags() })
}

////////// MUTATIONS //////////

export const useCreateVideoMutation = (): UseMutationResult<IVideoResultCreate, unknown, IVideo> => {
  return useMutation({ mutationFn: createVideo })
}

export const useUpdateVideoMutation = (): UseMutationResult<void, unknown, IVideoUpdate> => {
  return useMutation({ mutationFn: updateVideo })
}

export const useUpdateVideoThumbnailMutation = (): UseMutationResult<void, unknown, { fileId: string; id: string }> => {
  return useMutation({ mutationFn: updateVideoThumbnail })
}

export const useAddVideoToFavouriteMutation = (): UseMutationResult<
  IVideoTagResultItem[],
  unknown,
  { videoId: string }
> => {
  return useMutation({ mutationFn: addVideoToFavourite })
}

export const useDeleteVideoFromFavouriteMutation = (): UseMutationResult<
  IVideoTagResultItem[],
  unknown,
  { videoId: string }
> => {
  return useMutation({ mutationFn: deleteVideoFromFavourite })
}
